import '@reduxjs/toolkit';
import { PaymentElement } from '@stripe/react-stripe-js';
import { Button, ScrollbarContainer } from 'UI';
import { TermEnum as uiTermEnum } from 'UI/PriceLabel/PriceLabel';
import {
  CheckboxSkeletonWrapper,
  CheckboxSquareSkeleton,
  CouponAppliedIconSkeleton,
  CouponAppliedSkeleton,
  CouponAppliedTextSkeleton,
  CouponBoxSkeleton,
  CouponSkeletonContainer,
  InstallmentPriceSkeleton,
  LifetimeCouponAppliedSkeleton,
  LifetimeCouponBoxSkeleton,
  PriceSkeleton,
  PricingRowSkeleton,
  RemoveCouponButtonSkeleton,
} from 'UI/Skeletons/CheckoutSkeletons';
import StyledText from 'UI/StyledText';
import {
  CouponDataDto,
  CouponDiscountTypeEnum,
  CouponResponseDtoDiscountTypeEnum,
  DirectionDto,
  StripePaymentSource,
  SubscribeDtoTypeEnum,
} from 'api/generated';
import { AxiosError } from 'axios';
import { getLevelName } from 'constant';
import { useAsyncAction, useGoogleAnalytics, useStripeUnauthorizedForm } from 'hooks';
import { useCoupon } from 'hooks';
import { useStripeDefault } from 'hooks/useStripeDefault';
import { TermEnum } from 'pages/CreateUser/CreateUser';
import { CreateUserModalsEnum, IframeEvents } from 'pages/CreateUser/CreateUser';
import { MonetizationLightModeProvider, UnauthorizedStripeProvider } from 'providers';
import React, { FC, useEffect, useRef, useState } from 'react';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import styled, { css } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import { safePostMessage } from 'utils';

import DefaultModal from '../../../UI/Modals/DefaultModal';
import { DefaultModalProps } from '../../../UI/Modals/DefaultModal/DefaultModal';
import { Coupon, CouponForCertificate, Price, PriceInstallment, Secure } from '../PaymentModal/components';
import { SubscribeDtoType } from '../PaymentModal/components/CouponForCertificate/components/SuccessCoupon/SuccessCoupon';
import { AuthorizedInput } from './components';

interface CouponInfo {
  promoCode?: string;
  discountValue?: number;
  discountAmount?: number;
  discountType?: CouponResponseDtoDiscountTypeEnum;
  couponErrorMessage?: string | null;
  isSuccessCoupon?: boolean;
}

declare const dataLayer: any;

type SignUpModalProps = {
  email: string;
  openSuccessModal?: () => void;
  courseName: string;
  subscribeType: SubscribeDtoTypeEnum;
  currentPrice: number;
  showCloseButton?: boolean;
  oldPrice: number;
  isCertificate: boolean;
  term: TermEnum;
  courseId: string;
  defaultCoupon: any;
  currentModal: CreateUserModalsEnum;
  defaultFirstCouponInfo: any;
  impactClickId?: string;
  googleClickId?: string;
  abId?: string;
  logoOverride?: string | null;
} & DefaultModalProps;

const SignUpModal: FC<SignUpModalProps> = ({
  email,
  openSuccessModal,
  courseName,
  showCloseButton,
  subscribeType,
  isCertificate,
  currentPrice,
  oldPrice,
  term,
  courseId,
  defaultCoupon,
  defaultFirstCouponInfo,
  impactClickId,
  googleClickId,
  abId,
  logoOverride,
  ...props
}) => {
  const planLevelName = getLevelName(subscribeType, isCertificate ? 'certificate' : 'default');
  // Debug logging for click IDs
  console.log('DEBUG - SignUpModal props - Click IDs:', {
    impactClickId,
    googleClickId,
    abId,
    logoOverride,
    receivedAt: new Date().toISOString(),
  });
  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);
  const [invoiceId, setInvoiceId] = useState('');
  const [isLoadingChangeFirstCoupon, setIsLoadingChangeFirstCoupon] = useState<boolean>(false);
  const [pendingCouponCode, setPendingCouponCode] = useState<string | null>(null);
  const [defaultCouponInfo, setDefaultCouponInfo] = useState<CouponInfo>({});
  const [defCop, setDefCop] = useState<string | undefined>(defaultCoupon);
  const [isLoadingDefaultCoupon, setIsLoadingDefaultCoupon] = useState<boolean>(false); // Start as false to show content instantly
  const isFirstMonthCouponAutoActivatedRef = useRef(false);
  const dispatch = useAppDispatch();
  const { getEventSubscriptionPayPressedGA } = useGoogleAnalytics();
  const [certificateFinalPrice, setCertificateFinalPrice] = useState<number | false>(false);
  // const isUseFirstMonthCoupon = !!defaultFirstCouponInfo.firstCouponId;
  const {
    couponState,
    isInvalidCoupon,
    removeCoupon,
    onCouponSubmit,
    setIsInvalidCoupon,
    isDefaultCoupon,
    onChangePromoCode,
    isCertificateLoading,
    isCheckPromoCodeLoading,
    isLoading: isDeleteLoading,
    isChangePromoCodeLoading,
  } = useCoupon({
    courseId,
    subscribeType,
    term,
    amount: currentPrice,
    defaultCouponCode: defaultCoupon,
    setClientSecret,
    setInvoiceId,
    setDefCop,
    isCertificate,
    setDefaultCouponInfo,
    invoiceId,
    isUpdate: false,
  });

  const { couponErrorMessage, isSuccessCoupon, discountAmount, discountValue, promoCode } = couponState;

  const [error, setError] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [direction, setDirection] = useState<DirectionDto | undefined>(undefined);
  const [getDirectionByUrl] = useAsyncAction(actions.direction.getDirectionByUrl);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);

  const useFirstMonthDiscount = useAppSelector(selectors.courses.selectUseFirstMonthDiscount);
  const signUpModalCourse = useAppSelector(selectors.courses.selectPublicCourse);
  const directionFromRedux = useAppSelector(selectors.direction.selectDirection); // Get direction from Redux
  const url = window.location.origin;
  const termOfUseUrl = direction?.termsOfServiceUrl;
  const privacyPolicyUrl = direction?.privacyPolicyUrl;

  // Use logo override if provided, otherwise fallback to direction logo
  const courseCheckoutLogoUrl =
    typeof (signUpModalCourse as any)?.courseCheckoutLogoUrl === 'string'
      ? ((signUpModalCourse as any).courseCheckoutLogoUrl as string).trim()
      : undefined;
  const logo = logoOverride || courseCheckoutLogoUrl || direction?.checkoutLogoUrl;
  const { getEventSuccessfulPurchaseForLandingGA, getEventSuccessfulSubscribeForLandingGA } = useGoogleAnalytics();

  const postMessage = (event: string, data?: any) => {
    safePostMessage({ event, data }, direction?.landingUrl);
  };

  const getCurrentDirection = async (url: string) => {
    const direction = await getDirectionByUrl({ url });

    setDirection(direction);
  };

  useEffect(() => {
    getCurrentDirection(url);
  }, [url]);

  useEffect(() => {
    const isInstallmentTerm = term === TermEnum.INSTALLMENT;
    const hasFirstMonthCoupon = !!defaultFirstCouponInfo?.firstCouponId;

    if (
      !isInstallmentTerm ||
      !hasFirstMonthCoupon ||
      useFirstMonthDiscount ||
      isFirstMonthCouponAutoActivatedRef.current
    ) {
      return;
    }

    isFirstMonthCouponAutoActivatedRef.current = true;
    dispatch(actions.courses.setUseFirstMonthDiscount(true));
  }, [defaultFirstCouponInfo?.firstCouponId, dispatch, term, useFirstMonthDiscount]);

  // Don't manually call payment APIs - let useStripeDefault handle it
  const getSecret = async () => {
    setIsLoadingCustomer(false);
  };

  useEffect(() => {
    getSecret();
  }, []);

  // useStripeUnauthorizedForm handles form submission
  const {
    handleSubmit: handleSubmitStripe,
    errorMessage,
    isAccountExists,
    isLoading: isLoadingStripe,
    disabled,
    isSucceeded,
    setStripe: setStripeInForm,
    setElements: setElementsInForm,
    stripe,
    elements,
  } = useStripeUnauthorizedForm(clientSecret, courseId);

  const handleChangePromoCodeWithDeferredApply = async (data: { coupon?: string }) => {
    const normalizedCoupon = (data?.coupon || '').trim();

    if (!normalizedCoupon) {
      await onChangePromoCode(data);
      return;
    }

    if (!invoiceId) {
      await onCouponSubmit({ coupon: normalizedCoupon });
      setPendingCouponCode(normalizedCoupon);
      return;
    }

    await onChangePromoCode({ coupon: normalizedCoupon });
    setPendingCouponCode(null);
  };

  useEffect(() => {
    if (!pendingCouponCode || !invoiceId || isChangePromoCodeLoading) {
      return;
    }

    onChangePromoCode({ coupon: pendingCouponCode });
    setPendingCouponCode(null);
  }, [pendingCouponCode, invoiceId, isChangePromoCodeLoading, onChangePromoCode]);

  const isInstallmentTerm = term === TermEnum.INSTALLMENT;
  const isLifetimeTerm = term === TermEnum.LIFE_TIME;
  const isPurchase = isLifetimeTerm || isInstallmentTerm;

  // Wrapper to handle Express Checkout events for UnauthorizedStripeProvider
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleExpressCheckoutConfirm = async (event: any) => {
    // Convert Express Checkout event to the format expected by handleSubmitStripe
    const formEvent = {
      ...event,
      elementType: 'expressCheckout',
      preventDefault: () => {}, // No-op for Express Checkout
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return handleSubmitStripe(formEvent as any);
  };

  // Fetch default coupon data immediately on mount (before Stripe loads)
  useEffect(() => {
    const fetchDefaultCoupon = async () => {
      // Use directionFromRedux first, fallback to local direction state
      const directionId = directionFromRedux?.id || direction?.id;

      console.log('DEBUG - useEffect for default coupon triggered:', {
        defaultCoupon,
        courseId,
        directionId,
        directionFromRedux: !!directionFromRedux,
        directionLocal: !!direction,
        hasPromoCode: !!defaultCouponInfo.promoCode,
      });

      if (defaultCoupon && courseId && directionId) {
        // Map UI term -> backend CheckPromocodeRequestDto.paymentPeriod
        // Backend expects: monthly | annual | lifetime | installment
        const paymentPeriod =
          term === TermEnum.MONTHLY
            ? 'monthly'
            : term === TermEnum.YEARLY
            ? 'annual'
            : term === TermEnum.INSTALLMENT
            ? 'installment'
            : 'lifetime';

        // Skip if we already fetched this coupon
        if (defaultCouponInfo.promoCode === defaultCoupon) {
          console.log('DEBUG - Coupon already fetched, skipping');
          // Don't call setIsLoadingDefaultCoupon here to prevent flash
          return;
        }

        console.log('DEBUG - Fetching default coupon on mount:', defaultCoupon);
        setIsLoadingDefaultCoupon(true);
        try {
          const checkPromoCodeAction = dispatch(
            actions.courses.checkPromoCodeThunk({
              courseId,
              promocode: defaultCoupon,
              paymentPeriod,
              level: subscribeType as any, // Use subscribeType directly (e.g., 'gold'), not levelMapping
              directionId,
            }),
          );

          const result = await checkPromoCodeAction;
          console.log('DEBUG - Check promocode result:', result);

          // @ts-ignore - Type narrowing for payload
          if (result.payload && 'valid' in result.payload && result.payload.valid && result.payload.coupon) {
            // @ts-ignore
            const coupon = result.payload.coupon;
            console.log('DEBUG - Default coupon fetched successfully:', {
              promoCode: coupon.promoCode,
              discountType: coupon.discountType,
              discountAmount: coupon.discountAmount,
            });

            let discountValue = 0;
            if (coupon.discountType === CouponDiscountTypeEnum.Amount) {
              discountValue = coupon.discountAmount;
              console.log('DEBUG - Amount discount calculated:', discountValue);
            } else if (coupon.discountType === CouponDiscountTypeEnum.Percentage) {
              discountValue = (currentPrice * coupon.discountAmount) / 100;
              console.log('DEBUG - Percentage discount calculated:', discountValue);
            }

            setDefaultCouponInfo({
              promoCode: coupon.promoCode,
              discountType: coupon.discountType,
              discountAmount: coupon.discountAmount,
              discountValue,
            });

            console.log('DEBUG - setDefaultCouponInfo called with:', {
              promoCode: coupon.promoCode,
              discountAmount: coupon.discountAmount,
              discountValue,
            });
          } else {
            console.log('DEBUG - Invalid coupon response:', result.payload);
          }
          setIsLoadingDefaultCoupon(false);
        } catch (error) {
          console.error('DEBUG - Error fetching default coupon:', error);
          setIsLoadingDefaultCoupon(false);
        }
      } else {
        console.log('DEBUG - Skipping coupon fetch - missing required data:', {
          hasDefaultCoupon: !!defaultCoupon,
          hasCourseId: !!courseId,
          hasDirectionId: !!directionId,
        });
        // Don't setIsLoadingDefaultCoupon(false) here - keep current state to prevent flash
      }
    };

    fetchDefaultCoupon();
    // Reduce dependencies to only essential ones that should trigger refetch
  }, [defaultCoupon, courseId, directionFromRedux?.id, term, subscribeType]);

  // Handle coupon data when received from payment APIs
  const handleCouponDataReceived = (coupons: CouponDataDto[]) => {
    console.log('DEBUG - handleCouponDataReceived called');
    console.log('DEBUG - Full coupons array:', JSON.stringify(coupons, null, 2));

    if (coupons && coupons.length > 0) {
      const couponData = coupons[0];
      console.log('DEBUG - First coupon data:', JSON.stringify(couponData, null, 2));

      if (couponData?.coupon) {
        const { discountType, discountAmount, promocode } = couponData.coupon;

        console.log('DEBUG - Coupon details:', {
          discountType,
          discountAmount,
          promocode,
          valid: couponData.valid,
        });

        // Use the currentPrice prop
        console.log('DEBUG - Discount calculation:', {
          currentPrice,
          discountType,
          discountAmount,
        });

        let discountValue = 0;
        if (discountType === CouponDiscountTypeEnum.Amount) {
          discountValue = discountAmount;
          console.log('DEBUG - Amount discount:', discountValue);
        } else if (discountType === CouponDiscountTypeEnum.Percentage) {
          discountValue = (currentPrice * discountAmount) / 100;
          console.log('DEBUG - Percentage discount:', discountValue);
        }

        const finalPriceCalc = Math.max(0, currentPrice - discountValue);
        console.log('DEBUG - Final price calculation:', {
          currentPrice,
          discountValue,
          finalPrice: finalPriceCalc,
        });

        setDefaultCouponInfo({
          promoCode: promocode,
          discountType,
          discountAmount,
          discountValue,
        });

        console.log('DEBUG - Set defaultCouponInfo to:', {
          promoCode: promocode,
          discountType,
          discountAmount,
          discountValue,
        });
      } else {
        console.log('DEBUG - No valid coupon data in response - couponData:', couponData);
      }
    } else {
      console.log('DEBUG - No coupons in response - coupons:', coupons);
    }
  };

  // useStripeDefault handles payment API calls (validates pricing & creates payment)
  // We don't need to use the returned values - the hook updates clientSecret via setClientSecret callback
  useStripeDefault({
    stripe,
    elements,
    courseId,
    promocode: (() => {
      const code = promoCode || defaultCoupon;
      console.log('DEBUG - Promocode being passed to useStripeDefault:', {
        defaultCoupon,
        promoCode,
        finalCode: code,
        term,
        isPurchase,
      });
      return code;
    })(),
    period: term as any, // Pass term for all payment types (lifetime, installment, monthly, annual)
    level: subscribeType as any,
    source: StripePaymentSource.LandingPage,
    isTrial: false,
    isCertificate,
    useFirstMonthDiscount,
    isUpdate: false,
    isPurchase: isPurchase,
    courseName,
    amount: currentPrice,
    subscribeType,
    clientSecret,
    setInvoiceId,
    setClientSecret,
    setTransactionId: () => {},
    isActivate: false,
    onCouponDataReceived: handleCouponDataReceived,
    abId,
    impactClickId,
    googleClickId,
  });

  const isLoading = isLoadingStripe;

  // Debug button state changes
  useEffect(() => {
    console.log('🔘 [BUTTON-STATE] Button state changed:', {
      disabled,
      clientSecret: !!clientSecret,
      isAuthorized,
      buttonDisabled: disabled || !clientSecret || !isAuthorized,
      reasons: {
        noStripe: disabled,
        noClientSecret: !clientSecret,
        notAuthorized: !isAuthorized,
      },
    });
  }, [disabled, clientSecret, isAuthorized]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setError('');
    getEventSubscriptionPayPressedGA();
    await handleSubmitStripe(e);
  };

  const pushPostMessage = async () => {
    if (!clientSecret) return;

    try {
      const paymentIntent = (await stripe?.retrievePaymentIntent(clientSecret))?.paymentIntent;
      const email = paymentIntent?.receipt_email;

      const data = {
        term,
        productType: planLevelName,
        courseName,
        courseId,
        currentPrice: finalPrice,
        promoCode,
        oldPrice,
        currency: paymentIntent?.currency,
        transactionId: paymentIntent?.id,
        created_date: paymentIntent?.created ? new Date(paymentIntent?.created * 1000).toString() : undefined,
      };

      if (email) {
        dispatch(actions.auth.setEmail(email));
      }

      postMessage(IframeEvents.SUCCESSFUL_PURCHASE, data);
    } catch (error) {
      console.error(error);
    }
  };

  // prettier-ignore
  const pushDataLayer = async () => {
    if (!clientSecret) {
      return;
    }
    console.log('before SignUpModal pushDataLayer - with customer data');
    
    // Get payment intent directly from Stripe
    const paymentIntent = (await stripe?.retrievePaymentIntent(clientSecret))?.paymentIntent;

    // Calculate lifetime value
    const lifetimeValue = calculateLifetimeValue();
    
    dataLayer.push({
      event: 'successful_purchase',
      transactionId: paymentIntent?.id,
      course_name: courseName,
      promo_code: promoCode,
      item_name: subscribeType,
      item_id: courseId,
      value: Number(lifetimeValue),
      term: term,
      enhanced_conversion_data: {
        email: email,
        gclid: getGclidFromURLorCookie() || ''
      },
      gclid: getGclidFromURLorCookie() || '',
      irclickid: getImpactClickIdFromURLorCookie() || ''
    });

    // Helper function to get cookie by name
    function getCookie(name: string) {
      const nameEQ = name + '=';
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
      return null;
    }

    // Helper function to extract gclid from URL parameters or cookies
    function getGclidFromURLorCookie() {
      // First try to get from passed prop (which came from URL)
      if (googleClickId) return googleClickId;
      
      // Then try to get from URL parameters directly
      const urlParams = new URLSearchParams(window.location.search);
      const gclidFromUrl = urlParams.get('gclid');
      if (gclidFromUrl) return gclidFromUrl;
      
      // If not in URL, try to get from Google's _gcl_aw cookie
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.startsWith('_gcl_aw=')) {
          const cookieValue = cookie.substring('_gcl_aw='.length);
          // The format is typically domain~gclid
          const parts = cookieValue.split('.');
          if (parts.length > 1) {
            return parts[parts.length - 1];
          }
          return cookieValue;
        }
      }
      return '';
    }

    // Helper function to get Impact click ID from URL or cookie
    function getImpactClickIdFromURLorCookie() {
      // First try to get from passed prop (which came from URL)
      if (impactClickId) return impactClickId;
      
      // Then try to get from URL parameters directly
      const urlParams = new URLSearchParams(window.location.search);
      const irclickidFromUrl = urlParams.get('irclickid');
      if (irclickidFromUrl) return irclickidFromUrl;

      // If not in URL, try to get from irclickid cookie
      return getCookie('irclickid') || '';
    }

    // Helper function to calculate lifetime value consistently
    function calculateLifetimeValue() {
      // For installment courses, calculate lifetime value (12 months total)
      if (isCertificate && isInstallmentTerm) {
        // Get the total price before any discount
        const totalAmount = currentPrice - (Number(defaultCouponInfo?.discountValue) || discountValue || 0);
        // For installment plan with first month discount
        if (useFirstMonthDiscount && defaultFirstCouponInfo?.firstCouponAmount) {
          const perMonth = totalAmount / 12;
          const firstMonthDiscount =
            defaultFirstCouponInfo.firstCouponType === CouponDiscountTypeEnum.Amount
              ? defaultFirstCouponInfo.firstCouponAmount
              : (perMonth * defaultFirstCouponInfo.firstCouponAmount) / 100;
          // Total amount with first month discount applied
          return totalAmount - firstMonthDiscount;
        } else {
          return totalAmount;
        }
      } else if (term === TermEnum.MONTHLY) {
        // For monthly subscriptions, calculate annual value (12 months)
        return finalPrice * 2.5;
      } else if (term === TermEnum.YEARLY) {
        // For yearly subscriptions, value is already annual
        return finalPrice;
      }
      // For LIFETIME, keep the finalPrice as is
      return finalPrice;
    }
  };

  useEffect(() => {
    if (isSucceeded) {
      const onPaymentConfirm = async () => {
        try {
          setError('');
          openSuccessModal?.();
          pushDataLayer();
          pushPostMessage();
          term === TermEnum.LIFE_TIME
            ? getEventSuccessfulPurchaseForLandingGA()
            : getEventSuccessfulSubscribeForLandingGA();
        } catch (error) {
          setError((error as AxiosError).message);
        }
      };

      onPaymentConfirm();
    }
  }, [isSucceeded]);

  const finalPrice =
    (defaultCouponInfo?.discountValue || discountValue) && currentPrice
      ? currentPrice - (defaultCouponInfo?.discountValue || discountValue)
      : currentPrice;

  return (
    <MonetizationLightModeProvider>
      <DefaultModal
        {...props}
        headerContainerCSS={headerContainerCSS}
        containerCSS={containerCSS}
        showCloseButton={showCloseButton}
        titleCSS={titleCSS}
        modalCSS={modalCSS}
        shouldCloseOnOverlayClick={false}
        closeButtonCSS={closeButtonCSS}>
        <DescriptionContainer>
          <LogoContainer>
            {/* Reserve space for logo to prevent layout shift */}
            {logo ? <LogoImg src={logo} /> : <LogoImgPlaceholder />}
          </LogoContainer>

          {isLoadingCustomer || isLoadingDefaultCoupon ? (
            // Show skeleton loaders while initial data or default coupon is being loaded
            <>
              {isCertificate && isInstallmentTerm ? <InstallmentPriceSkeleton /> : <PriceSkeleton />}

              <NameContainer>
                <CourseNameText>{courseName}</CourseNameText>
                <CourseSubNameText>{planLevelName}</CourseSubNameText>
              </NameContainer>

              {/* Big white pricing box skeleton - only for installment */}
              {isCertificate && isInstallmentTerm ? (
                <>
                  <CouponSkeletonContainer>
                    {/* Ribbon skeleton (first month discount) */}
                    <CheckboxSkeletonWrapper>
                      <CheckboxSquareSkeleton />
                    </CheckboxSkeletonWrapper>

                    <CouponBoxSkeleton>
                      <PricingRowSkeleton />
                      <PricingRowSkeleton />
                      <PricingRowSkeleton />
                      <PricingRowSkeleton />
                      <PricingRowSkeleton />
                      <PricingRowSkeleton />
                    </CouponBoxSkeleton>

                    {/* Coupon applied skeleton - 337px for installment */}
                    <CouponAppliedSkeleton>
                      <CouponAppliedIconSkeleton />
                      <CouponAppliedTextSkeleton />
                      <RemoveCouponButtonSkeleton />
                    </CouponAppliedSkeleton>
                  </CouponSkeletonContainer>
                </>
              ) : (
                <>
                  {/* Smaller white box skeleton for lifetime/subscription */}
                  <LifetimeCouponBoxSkeleton>
                    <PricingRowSkeleton />
                    <PricingRowSkeleton />
                  </LifetimeCouponBoxSkeleton>

                  {/* Coupon applied skeleton - 263px for lifetime */}
                  <LifetimeCouponAppliedSkeleton>
                    <CouponAppliedIconSkeleton />
                    <CouponAppliedTextSkeleton />
                    <RemoveCouponButtonSkeleton />
                  </LifetimeCouponAppliedSkeleton>
                </>
              )}
            </>
          ) : (
            <>
              {isCertificate && isInstallmentTerm ? (
                <PriceInstallment
                  firstMonthDiscountAmount={defaultFirstCouponInfo.firstCouponAmount}
                  isFirstDiscount={useFirstMonthDiscount}
                  isInstallmentTerm={isInstallmentTerm}
                  amount={currentPrice}
                  isAmountFirstDiscountType={defaultFirstCouponInfo?.firstCouponType === CouponDiscountTypeEnum.Amount}
                  isDefaultCoupon={isDefaultCoupon}
                  discountValue={defaultCouponInfo.discountValue || discountValue}
                />
              ) : (
                <Price
                  amount={currentPrice}
                  term={term as uiTermEnum}
                  isDefaultCoupon={isDefaultCoupon}
                  discountValue={defaultCouponInfo.discountValue || discountValue}
                />
              )}

              <NameContainer>
                <CourseNameText>{courseName}</CourseNameText>
                <CourseSubNameText>{planLevelName}</CourseSubNameText>
              </NameContainer>

              {isCertificate && isInstallmentTerm ? (
                <CouponForCertificate
                  subscribeType={subscribeType as SubscribeDtoType}
                  isDefaultCoupon={isDefaultCoupon || Boolean(defCop)}
                  defaultCouponCode={defCop || defaultCoupon}
                  setIsLoadingChangeFirstCoupon={setIsLoadingChangeFirstCoupon}
                  isSuccessCoupon={defaultCouponInfo?.isSuccessCoupon || isSuccessCoupon || Boolean(defCop)}
                  courseName={courseName || ''}
                  setCertificateFinalPrice={setCertificateFinalPrice}
                  removeCoupon={removeCoupon}
                  amount={currentPrice}
                  setClientSecret={setClientSecret}
                  setInvoiceId={setInvoiceId}
                  oldAmount={oldPrice}
                  onCouponSubmit={onCouponSubmit}
                  isLoadingPromocode={isCheckPromoCodeLoading || isLoading || isChangePromoCodeLoading}
                  promoCode={defaultCouponInfo?.promoCode || promoCode}
                  invoiceId={invoiceId}
                  discountAmount={defaultCouponInfo?.discountAmount || discountAmount}
                  discountValue={defaultCouponInfo?.discountValue || discountValue}
                  couponErrorMessage={defaultCouponInfo.couponErrorMessage || couponErrorMessage}
                  invalidCoupon={isInvalidCoupon}
                  setClearCoupon={() => setIsInvalidCoupon(false)}
                  handleChangePromoCode={handleChangePromoCodeWithDeferredApply}
                  disable={isLoading || isChangePromoCodeLoading}
                  firstMonthDiscountId={defaultFirstCouponInfo?.firstCouponId}
                  firstMonthDiscountAmount={defaultFirstCouponInfo?.firstCouponAmount}
                  firstMonthDiscountType={defaultFirstCouponInfo?.firstCouponType}
                />
              ) : (
                <Coupon
                  subscribeType={planLevelName}
                  isDefaultCoupon={Boolean(defCop) || isDefaultCoupon}
                  defaultCouponCode={defCop || defaultCoupon}
                  isSuccessCoupon={defaultCouponInfo?.isSuccessCoupon || isSuccessCoupon || Boolean(defCop)}
                  courseName={courseName || ''}
                  removeCoupon={removeCoupon}
                  setCertificateFinalPrice={setCertificateFinalPrice}
                  oldAmount={oldPrice}
                  promoCode={defaultCouponInfo?.promoCode || promoCode}
                  amount={currentPrice}
                  onCouponSubmit={onCouponSubmit}
                  discountAmount={defaultCouponInfo?.discountAmount || discountAmount}
                  discountValue={defaultCouponInfo?.discountValue || discountValue}
                  couponErrorMessage={defaultCouponInfo.couponErrorMessage || couponErrorMessage}
                  invalidCoupon={isInvalidCoupon}
                  setClearCoupon={() => setIsInvalidCoupon(false)}
                  handleChangePromoCode={handleChangePromoCodeWithDeferredApply}
                  disable={isDeleteLoading || isChangePromoCodeLoading || isCertificateLoading}
                />
              )}
            </>
          )}

          {/* Coupon Section Skeletons */}
          {isLoadingCustomer ? (
            <>
              <CouponSkeletonContainer>
                {/* Ribbon skeleton */}
                <CheckboxSkeletonWrapper>
                  <CheckboxSquareSkeleton />
                </CheckboxSkeletonWrapper>

                {/* White box with pricing details skeleton */}
                <CouponBoxSkeleton>
                  <PricingRowSkeleton style={{ width: '70%' }} />
                  <PricingRowSkeleton style={{ width: '85%' }} />
                  <PricingRowSkeleton style={{ width: '60%' }} />
                  <PricingRowSkeleton style={{ width: '75%' }} />
                  <PricingRowSkeleton style={{ width: '65%' }} />
                  <PricingRowSkeleton style={{ width: '55%' }} />
                </CouponBoxSkeleton>

                {/* Coupon applied message skeleton */}
                <CouponAppliedSkeleton>
                  <CouponAppliedIconSkeleton />
                  <CouponAppliedTextSkeleton />
                  <RemoveCouponButtonSkeleton />
                </CouponAppliedSkeleton>
              </CouponSkeletonContainer>
            </>
          ) : null}
        </DescriptionContainer>
        <Content>
          <Form onSubmit={handleSubmit}>
            <div>
              <StripeFormWrapper>
                <UnauthorizedStripeProvider
                  showEmptyState={true}
                  setElements={setElementsInForm}
                  setStripe={setStripeInForm}
                  onConfirm={handleExpressCheckoutConfirm}
                  clientSecret={clientSecret}>
                  <>
                    <Text>Or with Card/Cash App</Text>
                    <AuthorizedInput setIsAuthorized={setIsAuthorized} courseId={courseId} />
                    <PaymentElement
                      options={{
                        readOnly: !clientSecret || finalPrice <= 0,
                        terms: { card: 'never', usBankAccount: 'never', bancontact: 'never' },
                      }}
                    />
                  </>
                </UnauthorizedStripeProvider>
                <ErrorText>
                  {!isAccountExists ? (
                    error || errorMessage
                  ) : (
                    <div style={{ display: 'flex' }}>
                      <p>
                        We noticed that you already have a trial account with {email}. Please
                        <a
                          style={{
                            textDecoration: 'underline',
                            marginLeft: '3px',
                            marginRight: '3px',
                            fontWeight: 'bold',
                          }}
                          href={`https://study.traineracademy.org/sign-in?isLandingPage=true&courseId=${courseId}`}
                          // href={`http://localhost:3000/sign-in?isLandingPage=true&courseId=${courseId}`}
                          target="_blank"
                          rel="noreferrer">
                          log in here to purchase.
                        </a>
                      </p>
                    </div>
                  )}
                </ErrorText>
              </StripeFormWrapper>
            </div>
            <Secure />
            <Footer>
              <Button
                isLoading={
                  isLoading ||
                  isDeleteLoading ||
                  isChangePromoCodeLoading ||
                  isLoadingChangeFirstCoupon ||
                  isCertificateLoading
                }
                disabled={disabled || !clientSecret || !isAuthorized}
                type="submit"
                variant="primary">
                {`Enroll Now: $${certificateFinalPrice || finalPrice} USD`}
              </Button>
              <LinkContainer>
                <Link as="a" href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                  Privacy
                </Link>
                <Link as="a" href={termOfUseUrl} target="_blank" rel="noopener noreferrer">
                  Terms
                </Link>
              </LinkContainer>
              <SecureTextContainer>
                {term.toLowerCase() !== TermEnum.LIFE_TIME && (
                  <SecureText font="subhead_medium_13">Renews automatically. Cancel anytime.</SecureText>
                )}
              </SecureTextContainer>
            </Footer>
          </Form>
        </Content>
      </DefaultModal>
    </MonetizationLightModeProvider>
  );
};

export default SignUpModal;

const modalCSS = css`
  max-height: 100%;
  height: 100%;
  max-width: 668px;

  width: 100%;

  padding: 37px 0;

  ${respondToWidth.sm`
    padding: 0;
  `};
`;

const containerCSS = css`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: scroll;
  padding: 0;
  padding-block: 40px;
  padding-right: 4px;

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${respondToWidth.s`
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow-x: hidden;
    border-radius: 0;
  `};
`;

const headerContainerCSS = css`
  ${respondToWidth.s`
    margin-bottom: 28px;
  `};
`;

const closeButtonCSS = css`
  border: 1px solid;
  border-color: #ffffff;
  background-color: ${({ theme: { colors } }) => colors.primary[1]};
  ${respondToWidth.sm`
    top: 40px;
    right: 16px;
  `};
`;

const titleCSS = css`
  ${({ theme: { typography } }) => respondToWidth.sm`
    ${typography.title_3_bold_28};
  `};

  ${({ theme: { typography, colors } }) => respondToWidth.s`
    width: 100%;
    text-align: center;
    color: ${colors.primary[1]};
    ${typography.title_3_bold_28};
  `};
`;

const Content = styled(ScrollbarContainer)`
  width: 100%;
  padding-inline: 100px 96px;
  height: 100%;
  overflow: unset;

  ${respondToWidth.sm`
    padding-inline: 40px 36px;

  `};
  ${respondToWidth.s`
    padding-inline: 16px 12px;
  `};
`;

const ErrorText = styled(StyledText)`
  text-align: start;

  ${({ theme: { colors, typography } }) => css`
    color: ${colors.system.red};
    ${typography.body_basic_medium_14}

    ${respondToWidth.s`
      ${typography.footnote_semibold_12}
  `}
  `};
`;

const DescriptionContainer = styled.div`
  margin-bottom: 48px;
  margin-right: -4px;
  margin-top: -73px;
  background-color: ${({ theme: { colors } }) => colors.primary[1]};

  ${({ theme: { typography } }) => respondToWidth.sm`
    ${typography.title_3_bold_28};
    margin-bottom: 20px;
  `};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  gap: 16px;
`;

const Footer = styled.div`
  display: grid;
  grid-gap: 28px;
  height: fit-content;

  ${respondToWidth.s`
      grid-gap: 16px;
  `}
`;

const LinkContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const Link = styled(StyledText)`
  display: flex;
  justify-content: center;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 4px;

  color: ${({ theme: { colors } }) => colors.primary[1]};
`;

const Text = styled(StyledText)`
  text-align: center;
  margin-top: 20px;
  color: ${({ theme: { colors } }) => colors.neutrals[4]};
  ${({ theme: { typography } }) => typography.body_basic_medium_14};
`;

const StripeFormWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: inherit;
  justify-content: stretch;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 45px;
  margin-bottom: 35px;
  cursor: pointer;
  padding-right: 8px;
  min-height: 40px; /* Reserve minimum height for logo */

  ${respondToWidth.s`
  margin-top: 20px;
  margin-bottom: 23px;
`}
`;

const LogoImg = styled.img`
  max-width: 100%;
  max-height: 40px;
  display: block; /* Prevent extra space below image */
`;

const LogoImgPlaceholder = styled.div`
  width: 200px; /* Reserve reasonable width */
  height: 40px;
  /* Invisible placeholder to maintain layout space */
  opacity: 0;
  pointer-events: none;
`;

const NameContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
`;

const CourseNameText = styled.span`
  display: flex;
  justify-content: center;
  text-align: center;
  padding-bottom: 10px;
  cursor: pointer;
  ${({ theme: { colors, typography } }) => css`
    color: ${colors.neutrals[11]};
    ${typography.title_5_bold_20}
  `};
`;

const CourseSubNameText = styled.span`
  display: flex;
  height: 28px;
  padding: 0px 5px;
  margin-left: 10px;
  border-radius: 10px;
  margin-top: -10px;
  text-align: center;
  padding-bottom: 10px;
  cursor: default;
  ${({ theme: { colors, typography } }) => css`
    color: ${colors.primary[1]};
    background-color: ${colors.neutrals[11]};
    ${typography.title_5_bold_20}
  `};
`;

const SecureText = styled(StyledText)`
  color: ${({ theme: { colors } }) => colors.neutrals[4]};
  margin-left: 6px;
`;

const SecureTextContainer = styled.div`
  display: flex;
  justify-content: center;
`;
